import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BriefcaseBusiness, CircleUserRound, FilePlus2, FolderKanban } from "lucide-react";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const navItems = [
  { href: "/dashboard/employer", label: "Overview", icon: BriefcaseBusiness },
  { href: "/dashboard/stb/create", label: "Neue Anzeige", icon: FilePlus2 },
  { href: "/dashboard/stb/manage", label: "Verwalten", icon: FolderKanban },
  { href: "/dashboard/account", label: "Account", icon: CircleUserRound },
];

const arbeitsformen = ["Vollzeit", "Teilzeit", "Minijob", "Schicht", "HomeOffice", "Hybrid", "Befristet", "Unbefristet"];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const emptyForm = {
  beruf_id: "",
  beruf_text: "",
  titel: "",
  standort_land: "DE",
  standort_ort: "",
  arbeitsform: "Vollzeit",
  beschreibung: "",
  aufgaben: "",
  anforderungen: "",
  benefits: "",
  sprachen: "",
  gehalt_von: "",
  gehalt_bis: "",
  gehalt_text: "",
  onboarding_bonus: 1000,
  wohnung: 1,
  laufzeit_monate: 12,
};

const StbAdvCreate = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const editId = query.get("id");

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [berufQuery, setBerufQuery] = useState("");
  const [berufe, setBerufe] = useState([]);
  const [loadingBerufe, setLoadingBerufe] = useState(false);
  const [berufeError, setBerufeError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [createdId, setCreatedId] = useState(editId ? Number(editId) : null);
  const [suggestions, setSuggestions] = useState({});
  const [gptLoadingField, setGptLoadingField] = useState("");

  useEffect(() => {
    document.title = "Experts4Europe | Neue Anzeige";
  }, []);

  useEffect(() => {
    if (!editId) return;
    let mounted = true;
    axios
      .get(`${config.API_BASE_URL}/api/stb/employer/ads/${editId}`, { headers: authHeaders() })
      .then((res) => {
        if (!mounted) return;
        const ad = res.data || {};
        setForm({
          ...emptyForm,
          ...ad,
          onboarding_bonus: ad.onboarding_bonus ?? 1000,
          wohnung: Number(ad.wohnung ?? 1),
          laufzeit_monate: ad.laufzeit_monate ?? 12,
          sprachen: Array.isArray(ad.sprachen) ? ad.sprachen.join(", ") : "",
        });
        setCreatedId(Number(editId));
      })
      .catch(() => setError("Anzeige konnte nicht geladen werden."))
      .finally(() => {
        mounted = false;
      });
  }, [editId]);

  useEffect(() => {
    if (!berufQuery || berufQuery.trim().length < 2) {
      setBerufe([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setBerufeError("");
        setLoadingBerufe(true);
        const res = await axios.get(`${config.API_BASE_URL}/api/stb/berufe`, {
          params: { q: berufQuery.trim(), limit: 20 },
          headers: authHeaders(),
        });
        setBerufe(res.data || []);
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Berufssuche fehlgeschlagen.";
        setBerufeError(msg);
        setBerufe([]);
      } finally {
        setLoadingBerufe(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [berufQuery]);

  const contextPayload = useMemo(
    () => ({
      beruf_text: form.beruf_text,
      arbeitsform: form.arbeitsform,
      standort_ort: form.standort_ort,
      tonality: "professionell",
    }),
    [form.beruf_text, form.arbeitsform, form.standort_ort]
  );

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!form.beruf_text || !form.titel || !form.arbeitsform || !form.beschreibung) return "Bitte Pflichtfelder ausfuellen.";
    if (Number(form.onboarding_bonus) < 1000) return "Onboarding-Bonus muss mindestens 1000 sein.";
    if (Number(form.wohnung) !== 1) return "Wohnungsunterstuetzung ist verpflichtend.";
    return null;
  };

  const payload = () => ({
    ...form,
    onboarding_bonus: Number(form.onboarding_bonus || 0),
    wohnung: Number(form.wohnung || 0),
    laufzeit_monate: Number(form.laufzeit_monate || 0),
    gehalt_von: form.gehalt_von ? Number(form.gehalt_von) : null,
    gehalt_bis: form.gehalt_bis ? Number(form.gehalt_bis) : null,
    sprachen: form.sprachen
      ? form.sprachen.split(",").map((v) => v.trim()).filter(Boolean)
      : [],
  });

  const saveDraft = async () => {
    setError("");
    setMessage("");
    const v = validate();
    if (v) {
      setError(v);
      return null;
    }
    try {
      setSaving(true);
      if (createdId) {
        await axios.put(`${config.API_BASE_URL}/api/stb/employer/ads/${createdId}`, payload(), { headers: authHeaders() });
        setMessage("Draft gespeichert.");
        return createdId;
      } else {
        const res = await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads`, payload(), { headers: authHeaders() });
        const newId = res.data?.ad?.id || null;
        setCreatedId(newId);
        setMessage("Draft gespeichert.");
        return newId;
      }
    } catch (e) {
      const detail = e.response?.data?.detail ? ` ${e.response.data.detail}` : "";
      setError((e.response?.data?.message || "Speichern fehlgeschlagen.") + detail);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setError("");
    setMessage("");
    const v = validate();
    if (v) return setError(v);
    try {
      let id = createdId || Number(editId);
      if (!id) {
        id = await saveDraft();
      }
      if (!id) return;
      setPublishLoading(true);
      await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${id}/publish`, {}, { headers: authHeaders() });
      setCreatedId(id);
      setMessage("Anzeige wurde veroeffentlicht.");
    } catch (e) {
      setError(e.response?.data?.message || "Publish fehlgeschlagen.");
    } finally {
      setPublishLoading(false);
    }
  };

  const suggestForField = async (field) => {
    try {
      setGptLoadingField(field);
      const res = await axios.post(
        `${config.API_BASE_URL}/api/stb/ads/gpt/suggest`,
        { field, input: form[field] || "", context: contextPayload },
        { headers: authHeaders() }
      );
      setSuggestions((prev) => ({ ...prev, [field]: res.data }));
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "GPT Vorschlag fehlgeschlagen.";
      setError(msg);
    } finally {
      setGptLoadingField("");
    }
  };

  const suggestionCard = (field) => {
    const box = suggestions[field];
    if (!box) return null;
    return (
      <div className="mt-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Vorschlag</p>
        <p className="mt-1 whitespace-pre-wrap">{box.suggestion}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" className="dashboard-action-button" onClick={() => updateForm(field, box.suggestion || "")}>
            Uebernehmen
          </Button>
          {(box.alternatives || []).map((alt, idx) => (
            <Button key={`${field}_${idx}`} size="sm" variant="outline" className="dashboard-inline-button" onClick={() => updateForm(field, alt)}>
              Alternative {idx + 1}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <CockpitShell
      title="Neue Stellenanzeige"
      subtitle="Anzeige, Inhalt und Publish bleiben im selben light-first Arbeitgeber-Template."
      badge="Employer Ad Studio"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Ad Studio"
      shellDescription="Folgeflaeche im selben Hiring Control Deck mit klarer Formularbuehne und reduzierter Nebenrail."
      modeLabel="Create Flow"
      liveLabel="Employer"
      topSignal="Employer System"
      topContext="Anzeige erstellen"
      actions={
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/stb/manage")}>
            Zur Verwaltung
          </Button>
          <Button className="dashboard-action-button dashboard-action-button-secondary" onClick={saveDraft} disabled={saving}>
            {saving ? "Speichert..." : "Draft speichern"}
          </Button>
          <Button className="dashboard-action-button" onClick={publish} disabled={publishLoading}>
            {publishLoading ? "Publish..." : "Veroeffentlichen"}
          </Button>
        </>
      }
    >
      {message ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{message}</p> : null}
      {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Step {step} / 4</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((s) => (
            <Button key={s} variant={s === step ? "default" : "outline"} className={s === step ? "dashboard-action-button" : "dashboard-inline-button"} onClick={() => setStep(s)}>
              {s === 1 ? "Basis" : s === 2 ? "Inhalt" : s === 3 ? "Konditionen" : "Preview"}
            </Button>
          ))}
        </CardContent>
      </Card>

      {step === 1 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Basisdaten</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm text-slate-600">Beruf suchen</label>
              <Input value={berufQuery} onChange={(e) => setBerufQuery(e.target.value)} placeholder="z.B. Pflegefachkraft" className="dashboard-field" />
              {loadingBerufe ? <p className="mt-2 text-xs text-slate-500">Suche...</p> : null}
              {berufeError ? <p className="mt-2 text-xs text-rose-600">{berufeError}</p> : null}
              {berufe.length > 0 ? (
                <div className="mt-2 max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white">
                  {berufe.map((b) => (
                    <button key={b.id} type="button" className="w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => {
                      updateForm("beruf_id", b.id);
                      updateForm("beruf_text", b.beruf);
                      setBerufQuery(b.beruf);
                      setBerufe([]);
                    }}>
                      {b.beruf}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <Input value={form.beruf_text} onChange={(e) => updateForm("beruf_text", e.target.value)} placeholder="Beruf (Snapshot)" className="dashboard-field" />
            <Input value={form.titel} onChange={(e) => updateForm("titel", e.target.value)} placeholder="Anzeigentitel" className="dashboard-field" />
            <div className="grid gap-3 md:grid-cols-3">
              <Select value={form.arbeitsform} onValueChange={(v) => updateForm("arbeitsform", v)}>
                <SelectTrigger className="dashboard-field"><SelectValue placeholder="Arbeitsform" /></SelectTrigger>
                <SelectContent>{arbeitsformen.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.standort_land} onChange={(e) => updateForm("standort_land", e.target.value.toUpperCase())} placeholder="Land (DE)" className="dashboard-field" />
              <Input value={form.standort_ort} onChange={(e) => updateForm("standort_ort", e.target.value)} placeholder="Ort" className="dashboard-field" />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Inhalt + GPT Assist</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: "beschreibung", label: "Beschreibung" },
              { key: "aufgaben", label: "Aufgaben" },
              { key: "anforderungen", label: "Anforderungen" },
              { key: "gehalt_text", label: "Gehaltstext" },
            ].map((item) => (
              <div key={item.key}>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm text-slate-600">{item.label}</label>
                  <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => suggestForField(item.key)}>
                    {gptLoadingField === item.key ? "..." : "✨ Vorschlag"}
                  </Button>
                </div>
                <Textarea rows={5} value={form[item.key] || ""} onChange={(e) => updateForm(item.key, e.target.value)} className="dashboard-field dashboard-textarea" />
                {suggestionCard(item.key)}
              </div>
            ))}
            <Input value={form.sprachen} onChange={(e) => updateForm("sprachen", e.target.value)} placeholder="Sprachen, kommasepariert (z.B. Deutsch B2, Englisch B1)" className="dashboard-field" />
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm text-slate-600">Benefits</label>
                <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => suggestForField("benefits")}>
                  {gptLoadingField === "benefits" ? "..." : "✨ Vorschlag"}
                </Button>
              </div>
              <Textarea rows={4} value={form.benefits || ""} onChange={(e) => updateForm("benefits", e.target.value)} className="dashboard-field dashboard-textarea" />
              {suggestionCard("benefits")}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Konditionen</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <Input type="number" value={form.gehalt_von} onChange={(e) => updateForm("gehalt_von", e.target.value)} placeholder="Gehalt von" className="dashboard-field" />
            <Input type="number" value={form.gehalt_bis} onChange={(e) => updateForm("gehalt_bis", e.target.value)} placeholder="Gehalt bis" className="dashboard-field" />
            <Input value={form.gehalt_text} onChange={(e) => updateForm("gehalt_text", e.target.value)} placeholder="Gehaltstext (optional)" className="dashboard-field" />
            <Input type="number" min="1000" value={form.onboarding_bonus} onChange={(e) => updateForm("onboarding_bonus", e.target.value)} placeholder="Onboarding Bonus >= 1000" className="dashboard-field" />
            <Select value={String(form.wohnung)} onValueChange={(v) => updateForm("wohnung", Number(v))}>
              <SelectTrigger className="dashboard-field"><SelectValue placeholder="Wohnung" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Wohnungsunterstuetzung: Ja (Pflicht)</SelectItem>
                <SelectItem value="0">Nein (nicht erlaubt)</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={form.laufzeit_monate} onChange={(e) => updateForm("laufzeit_monate", e.target.value)} placeholder="Laufzeit Monate" className="dashboard-field" />
          </CardContent>
        </Card>
      ) : null}

      {step === 4 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Preview</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">{form.beruf_text || "-"}</Badge>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{form.arbeitsform || "-"}</Badge>
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Bonus {Number(form.onboarding_bonus || 0)}</Badge>
              <Badge variant="outline" className={Number(form.wohnung) === 1 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                Wohnung {Number(form.wohnung) === 1 ? "Ja" : "Nein"}
              </Badge>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900">{form.titel || "(Titel fehlt)"}</h2>
            <p><strong>Standort:</strong> {[form.standort_land, form.standort_ort].filter(Boolean).join(" / ") || "-"}</p>
            <p className="whitespace-pre-wrap">{form.beschreibung || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Aufgaben:</strong>{"\n"}{form.aufgaben || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Anforderungen:</strong>{"\n"}{form.anforderungen || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Benefits:</strong>{"\n"}{form.benefits || "-"}</p>
            <div className="flex flex-wrap gap-2">
              <Button className="dashboard-action-button" onClick={saveDraft}>Draft speichern</Button>
              <Button className="dashboard-action-button-success" onClick={publish}>Publish</Button>
              {createdId ? (
                <Button variant="outline" className="dashboard-inline-button" onClick={() => navigate(`/dashboard/stb/preview/${createdId}`)}>
                  In Vorschau-Seite oeffnen
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </CockpitShell>
  );
};

export default StbAdvCreate;
